import type { HandsFreeMode } from './handsFreeSteps';

/**
 * Hands-free preferences, kept on the device rather than in the account.
 *
 * Everything else the learner configures syncs, because "cards per session" and
 * "what goes on the front" are opinions about studying and travel with you.
 * These are not: they are facts about the machine in front of you. Whether
 * on-device recognition exists, which voices are installed and how long you
 * need to answer before the microphone gives up all differ between a desktop
 * and a phone, and syncing them would push a laptop's settings onto a handset
 * that cannot honour them.
 *
 * The cost is that a new device starts from the defaults. That is the right
 * trade here, and it keeps hands-free a client-only change with no API behind
 * it — which is also why it could ship to dev without a twenty-minute build.
 */

const KEY = 'handsFree.v1';

export interface HandsFreePrefs {
  mode: HandsFreeMode;
  /** Seconds the microphone stays open for one answer. */
  listenSeconds: number;
  /**
   * Refuse to recognise at all unless it happens on this device.
   *
   * Off by default, because Chrome's on-device packs are not everywhere yet and
   * a silent failure would look like a broken feature. On, it is honoured
   * absolutely — see useRecognition.
   */
  localOnly: boolean;
}

export const DEFAULT_PREFS: HandsFreePrefs = {
  mode: 'recall',
  listenSeconds: 6,
  localOnly: false,
};

const MODES: HandsFreeMode[] = ['recall', 'production', 'echo'];

/** Reads what is stored, repairing anything that does not look right. */
export function loadPrefs(): HandsFreePrefs {
  if (typeof localStorage === 'undefined') return DEFAULT_PREFS;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULT_PREFS;
    const parsed = JSON.parse(raw) as Partial<HandsFreePrefs>;
    return {
      mode: MODES.includes(parsed.mode as HandsFreeMode)
        ? (parsed.mode as HandsFreeMode)
        : DEFAULT_PREFS.mode,
      listenSeconds:
        typeof parsed.listenSeconds === 'number' && Number.isFinite(parsed.listenSeconds)
          ? Math.min(Math.max(parsed.listenSeconds, 2), 20)
          : DEFAULT_PREFS.listenSeconds,
      localOnly: parsed.localOnly === true,
    };
  } catch {
    // Private browsing, cleared storage, or something else wrote here first.
    return DEFAULT_PREFS;
  }
}

export function savePrefs(prefs: HandsFreePrefs): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch {
    // Not worth interrupting a drill over; the defaults still work.
  }
}
